import { useQuery } from '@tanstack/react-query'
import { Col, Flex, Row, Spin } from 'antd'
import DOMPurify from 'dompurify'

import { useParams } from '@suntex.core/nav'
import { useFramework } from '@suntex.fim/framework'
import { ResourceList } from '@suntex.fim/ui'

import ExploreLink from './ExploreLink'

const TITLE_INFO_INDEX_SEND_HOME_AWARDS = 2
const TITLE_INFO_INDEX_INFO_FOR_PARENTS = 1
const TITLE_INFO_INDEX_FAMILY_LINK = 0

/**
 * returns JSX for home connection
 */
function HomeConnection() {
  const { api } = useFramework()
  const { teamId } = useParams()
  const { data: webflowHomeConnection, isLoading: isLoadingInfo } = useQuery({
    queryKey: ['webflow', 'homeConnectionInfo'],
    queryFn: () => api.get_WebFlow_HomeConnectionInfo({})
  })
  const {
    data: webflowHomeConnectionFamilyLink,
    isLoading: isLoadingFamilyLink
  } = useQuery({
    queryKey: ['webflow', 'homeConnectionFamilyLink'],
    queryFn: () => api.get_WebFlow_HomeConnectionFamilyLink({})
  })

  // Helper function to update href for <a rel="Recognition">
  function updateRecognitionLink(html: string) {
    if (!html) return html
    const recognitionUrl = `/team/${teamId}/learning-center/recognition`
    const parser = new window.DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const anchors = doc.querySelectorAll(
      'a[rel="Recognition"][href="#"], a[rel=\'Recognition\'][href="#"]'
    )
    // No matching <a> tags found, return original html
    if (anchors.length === 0) {
      return html
    }
    // if matching <a> tags found, update href attribute
    anchors.forEach((anchor) => {
      anchor.setAttribute('href', recognitionUrl)
    })
    return doc.body.innerHTML
  }

  if (
    isLoadingInfo ||
    isLoadingFamilyLink ||
    !webflowHomeConnection ||
    !webflowHomeConnectionFamilyLink
  )
    return <Spin />
  return (
    <Flex vertical>
      {/* heading */}
      <div className="main-navigation">
        <h3 className="main-navigation__heading">Home Connection</h3>
      </div>
      {/* list */}
      <Row className="resources-column-wrapper" gutter={40}>
        <Col className="resources-column" xs={24} md={12}>
          <ResourceList
            title={
              webflowHomeConnection.titleInfoFieldData.length > 0
                ? (webflowHomeConnection.titleInfoFieldData[
                    TITLE_INFO_INDEX_SEND_HOME_AWARDS
                  ]?.name ?? '')
                : ''
            }
          >
            <ul className="resources-list">
              {webflowHomeConnection.infoForParentsFieldData.map((item) => (
                <li key={item.name}>
                  <div className="resources-item">
                    <div className="resources-item__title fw-bold">
                      <ExploreLink
                        url={item['title-link'] ?? ''}
                        title={item.name ?? ''}
                      />
                    </div>
                    <span className="resources-item__subtitle text-chalkboard-txt">
                      {item['title-sub-text'] ?? ''}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </ResourceList>
          <ResourceList
            title={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              webflowHomeConnection.titleInfoFieldData &&
              webflowHomeConnection.titleInfoFieldData.length > 0
                ? (webflowHomeConnection.titleInfoFieldData[
                    TITLE_INFO_INDEX_INFO_FOR_PARENTS
                  ]?.name ?? '')
                : ''
            }
          >
            <div className="resources-list">
              <p
                className="fw-bold"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    updateRecognitionLink(
                      webflowHomeConnection.titleInfoFieldData[
                        TITLE_INFO_INDEX_INFO_FOR_PARENTS
                      ]?.['parents-descriptions'] ?? ''
                    )
                  )
                }}
              />
            </div>
          </ResourceList>
        </Col>
        <Col className="resources-column" xs={24} md={12}>
          <ResourceList
            title={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              webflowHomeConnection.titleInfoFieldData &&
              webflowHomeConnection.titleInfoFieldData.length > 0
                ? (webflowHomeConnection.titleInfoFieldData[
                    TITLE_INFO_INDEX_FAMILY_LINK
                  ]?.name ?? '')
                : ''
            }
          >
            <ul className="resources-list">
              <li>
                <p
                  className="text-chalkboard-txt fw-bold"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      webflowHomeConnection.titleInfoFieldData[
                        TITLE_INFO_INDEX_FAMILY_LINK
                      ]?.['parents-descriptions'] ?? ''
                    )
                  }}
                />
              </li>
              {webflowHomeConnectionFamilyLink.categories
                .sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
                .map((item) => (
                  <li key={item.name}>
                    {/* eslint-disable-next-line no-magic-numbers */}
                    <Row gutter={[10, 10]}>
                      <Col span={24}>
                        <h6 className="text-sm">
                          {item.name ?? ''}
                          {item['categories-subtext'] && (
                            <span className="text-chalkboard-txt">
                              {` ${item['categories-subtext']}`}
                            </span>
                          )}
                        </h6>
                      </Col>
                      {item.subcategory
                        ?.sort(
                          (a, b) => a.name?.localeCompare(b.name ?? '') ?? 0
                        )
                        .map((subItem) => (
                          <Col key={subItem.name} xs={24} md={12}>
                            <div className="resources-item">
                              <div className="resources-item__title fw-bold">
                                <ExploreLink
                                  url={subItem.link ?? ''}
                                  title={`${subItem.name ?? ''}`}
                                />
                              </div>
                            </div>
                          </Col>
                        ))}
                    </Row>
                  </li>
                ))}
            </ul>
          </ResourceList>
        </Col>
      </Row>
    </Flex>
  )
}

export default HomeConnection
